/**
 * V2 Copy Block Section Field Configuration
 *
 * V2 equivalent of bodySectionConfig.ts + titleSubtitleSectionConfig logic.
 * Maps V2 StepNode.content.copy and content.hero paths.
 *
 * V2 path changes from V1:
 * - title.l10n → content.copy.title.translations
 * - title.kind → content.copy.title.kind ('default'→'static', 'template'→'dynamic')
 * - subtitle1.l10n → content.copy.subtitle.translations
 * - subtitle2: removed from V2 CopyBlock (v2.18.0)
 * - image.l10n → content.hero.translations
 * - text alignment: 'left'→'start', 'right'→'end'
 *
 * Sprint: schema-v2-migration, Sprint 3
 */

import type { FieldConfig } from '../types/editorFieldTypes';
import { createTextStyleFields, remapTextFieldsForV2 } from './atomicFields';
import {
  NUMBER_0_50_OPTIONS,
  NUMBER_0_100_OPTIONS,
  NUMBER_0_500_OPTIONS,
  SCALE_MODE_OPTIONS,
} from './atomicFields/options';

/**
 * V2 Title field configuration
 *
 * Path: content.copy.title
 * Translations: content.copy.title.translations
 * Kind: content.copy.title.kind ('static'/'dynamic')
 * Appearance: content.copy.title.appearance
 */
export const V2_TITLE_CONFIG: FieldConfig = {
  path: 'content.copy.title',
  label: 'Title',
  type: 'templateText',
  guideKey: 'screenedit-copy-block-title',
  l10nPath: 'content.copy.title',
  defaultValue: '',
  disableBehavior: 'null',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.copy.title')),
  paddingConfig: {
    basePath: 'content.copy.title.layout.insets',
    label: 'Padding',
  },
  templateConfig: {
    kindPath: 'content.copy.title.kind',
    labelsPath: 'content.copy.title.templateParams.labels',
    linksPath: 'content.copy.title.templateParams.links',
    countdownsPath: 'content.copy.title.templateParams.countdowns',
  },
};

/**
 * V2 Subtitle field configuration
 *
 * Path: content.copy.subtitle
 * Translations: content.copy.subtitle.translations
 */
export const V2_SUBTITLE_CONFIG: FieldConfig = {
  path: 'content.copy.subtitle',
  label: 'Subtitle',
  type: 'templateText',
  guideKey: 'screenedit-copy-block-subtitle',
  l10nPath: 'content.copy.subtitle',
  defaultValue: '',
  disableBehavior: 'null',
  styleFields: remapTextFieldsForV2(createTextStyleFields('content.copy.subtitle')),
  paddingConfig: {
    basePath: 'content.copy.subtitle.layout.insets',
    label: 'Padding',
  },
  templateConfig: {
    kindPath: 'content.copy.subtitle.kind',
    labelsPath: 'content.copy.subtitle.templateParams.labels',
    linksPath: 'content.copy.subtitle.templateParams.links',
    countdownsPath: 'content.copy.subtitle.templateParams.countdowns',
  },
};

/**
 * V2 Hero Image field configuration
 *
 * Path: content.hero
 * Translations: content.hero.translations (localized images)
 * Appearance: content.hero.appearance (scaleMode, dimensions)
 */
export const V2_HERO_IMAGE_CONFIG: FieldConfig = {
  path: 'content.hero',
  label: 'Hero Image',
  type: 'imageUpload',
  guideKey: 'screenedit-copy-block-hero',
  l10nPath: 'content.hero',
  styleFields: [
    {
      path: 'content.hero.appearance.scaleMode',
      label: 'Scale Mode',
      type: 'select',
      guideKey: 'screenedit-copy-block-hero-scale-mode',
      options: [...SCALE_MODE_OPTIONS],
      defaultValue: 'scaleAspectFit',
      isNullable: true,
    },
    {
      path: 'content.hero.appearance.dimensions.width',
      label: 'Width',
      type: 'numberCombo',
      guideKey: 'screenedit-copy-block-hero-width',
      options: NUMBER_0_500_OPTIONS,
      defaultScrollValue: 100,
      isNullable: true,
    },
    {
      path: 'content.hero.appearance.dimensions.height',
      label: 'Height',
      type: 'numberCombo',
      guideKey: 'screenedit-copy-block-hero-height',
      options: NUMBER_0_500_OPTIONS,
      defaultScrollValue: 100,
      isNullable: true,
    },
    {
      path: 'content.hero.appearance.dimensions.heightPercentage',
      label: 'Height %',
      type: 'numberCombo',
      guideKey: 'screenedit-copy-block-hero-height-percentage',
      options: NUMBER_0_100_OPTIONS,
      defaultScrollValue: 50,
      isNullable: true,
    },
    {
      path: 'content.hero.appearance.cornerRadii.uniform',
      label: 'Corner Radius',
      type: 'numberCombo',
      guideKey: 'screenedit-copy-block-hero-corner-radius',
      options: NUMBER_0_50_OPTIONS,
      defaultScrollValue: 0,
      isNullable: true,
    },
  ],
  paddingConfig: {
    basePath: 'content.hero.layout.insets',
    label: 'Padding',
  },
};

/**
 * Screen types that include hero image in copy block section
 */
export const HERO_SCREEN_TYPES: ReadonlySet<string> = new Set([
  'HeroContent',
  'WheelPicker',
  'HeroList',
]);

/**
 * All V2 copy block field configurations in render order
 */
export const V2_COPY_BLOCK_FIELD_CONFIGS: FieldConfig[] = [
  V2_TITLE_CONFIG,
  V2_SUBTITLE_CONFIG,
];
